import { TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../common/contexts/userContext";
import { createLeague, uploadLeagueImage } from "../../services/leagueService";
import { League } from "../../types/League";
import CreateLeagueUploadPhoto from "./CreateLeagueUploadPhoto";
import "./MobileWizard.scss";
import NextButton from "./NextButton";
interface CreateLeaguePanelProps {}

const CreateLeaguePanel: React.FC<CreateLeaguePanelProps> = () => {
  const navigate = useNavigate();
  const [newLeagueName, setNewLeagueName] = useState<string>("");
  const [leagueDescription, setLeagueDescription] = useState<string>("");
  const { user, setUser } = useUser();
  const [imgBlobLink, setImgBlobLink] = useState<string>("");
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");

  function withCacheBust(url: string) {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${Date.now()}`;
  }

  function handleChangeName(event: React.ChangeEvent<HTMLInputElement>) {
    setNewLeagueName(event.target.value);
  }
  function handleChangeLeague(event: React.ChangeEvent<HTMLInputElement>) {
    setLeagueDescription(event.target.value);
  }

  async function handleCreateLeague() {
    try {
      setUploadError("");
      const data = await createLeague({
        name: newLeagueName,
        description: leagueDescription,
        leagueImageUrl: "",
        adminId: user.id,
      });
      const newLeague: League = {
        id: data.id,
        name: data.name,
        description: data.description,
        userIds: data.userIds,
        leagueImageUrl: data.leagueImageUrl,
        adminId: user.id,
        topPoints: data.topPoints,
        topScorer: user.username,
        usersCount: data.usersCount,
      };
      setUser((prev) => ({ ...prev, leagues: [...prev.leagues, newLeague] }));
      setImgBlobLink(newLeague.leagueImageUrl);
      if (uploadedImage) {
        try {
          const response = await uploadLeagueImage(data.id, uploadedImage);
          const refreshedUrl = withCacheBust(response.leagueImageUrl);
          setImgBlobLink(refreshedUrl);
          setUser((prev) => ({
            ...prev,
            leagues: prev.leagues.map((league) =>
              league.id === data.id
                ? { ...league, leagueImageUrl: refreshedUrl }
                : league
            ),
          }));
        } catch {
          setUploadError(
            "League created, but image upload failed. You can retry from league management."
          );
        }
      }

      navigate("/leagues");
    } catch {
      alert("League couldn't be created - try once more");
    }
  }

  return (
    <div className="createLeague__root">
      <div className="stepBody" >
        <div className="wizardStep__fieldWrap">
          <Typography variant="h4" className="createLeague__heading"> Create your league</Typography>
          <Typography className="createLeague__intro">
            You can create your league in order to compete with your friends or
            other users. After creating a league either you can invite members
            or members may find your league and ask you for joining. Remember
            that only the founder of league may be the admin.
          </Typography>
          <Typography className="wizardStep__label"> League Name </Typography>

          <TextField
            className="createLeague__nameField"
            variant="outlined"
            InputProps={{ className: "customedInput" }}
            inputProps={{ maxLength: 20 }}
            type="text"
            value={newLeagueName}
            onChange={handleChangeName}
            name="name"
            placeholder="(at least 3 letters)"
          />
          <Typography className="createLeague__descLabel"> League Description </Typography>

          <TextField
            className="createLeague__descField"
            InputProps={{ className: "customedInputDescription" }}
            inputProps={{ maxLength: 150 }}
            type="text"
            value={leagueDescription}
            onChange={handleChangeLeague}
            name="name"
            placeholder="(optional)"
            rows={3}
            multiline
            maxRows={3}
          />
          <div className="createLeague__uploadWrap">
            <CreateLeagueUploadPhoto
              buttonLabel="Upload a League Photo"
              imgBlobLink={imgBlobLink}
              setUploadedImage={setUploadedImage}
              uploadedImage={uploadedImage}
            />
            {uploadError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </div>
        </div>
        <NextButton
          label="Create League"
          createLeague={handleCreateLeague}
          disabled={newLeagueName.length < 2}
        />
      </div>
    </div>
  );
};

export default CreateLeaguePanel;
